import React, { useState, useEffect } from 'react';
import { Plus, Search, FileText, Edit2, Trash2, X, CheckCircle, AlertCircle, BookOpen } from 'lucide-react';
import { sopsAPI } from '../utils/api';
import toast from 'react-hot-toast';

function SOPModal({ sop, onClose, onSave }) {
  const isEdit = !!sop;
  const [form, setForm] = useState({
    title: sop?.title || '',
    content_html: sop?.content_html || ''
  });
  const [loading, setLoading] = useState(false);

  const save = async () => {
    if (!form.title || !form.content_html) return toast.error('Both title and content are required');
    setLoading(true);
    try {
      if (isEdit) {
        await sopsAPI.update(sop.id, form);
        toast.success('SOP updated successfully');
      } else {
        await sopsAPI.create(form);
        toast.success('SOP created successfully');
      }
      onSave();
    } catch (err) {
      toast.error('Failed to save SOP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <div className="modal-title">{isEdit ? 'Edit Procedure' : 'Create New Procedure'}</div>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="form-group">
            <label className="form-label">SOP Title</label>
            <input 
              className="form-input" 
              value={form.title} 
              onChange={e => setForm({ ...form, title: e.target.value })} 
              placeholder="e.g. Generator Emergency Shutdown Procedure" 
            />
          </div>
          <div className="form-group">
            <label className="form-label">Procedure Content (HTML/Markdown)</label>
            <textarea 
              className="form-textarea" 
              value={form.content_html} 
              onChange={e => setForm({ ...form, content_html: e.target.value })} 
              placeholder="Enter detailed steps and safety instructions..." 
              rows={12}
            />
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
              Supports basic HTML/text for mobile-friendly rendering.
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={loading}>
            {loading ? <div className="spinner" /> : <CheckCircle size={15} />}
            {isEdit ? 'Update SOP' : 'Create SOP'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SOPsPage() {
  const [sops, setSops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingSop, setEditingSop] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await sopsAPI.list();
      setSops(res.data.sops);
    } catch (err) {
      toast.error('Failed to load SOPs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this SOP? This will unlink it from all assets.')) return;
    try {
      await sopsAPI.delete(id);
      toast.success('SOP deleted');
      load();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const filtered = sops.filter(s => s.title.toLowerCase().includes(search.toLowerCase()));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: 'var(--text)' }}>Standard Operating Procedures</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Manage safety and operational guidelines linked to assets.</p>
        </div>
        <button className="btn btn-primary" onClick={() => { setEditingSop(null); setShowModal(true); }}>
          <Plus size={16} /> New SOP
        </button>
      </div>

      {/* Stats/Tools */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
        <div className="card" style={{ padding: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
          <div style={{ background: 'var(--accent-muted)', color: 'var(--accent)', padding: 8, borderRadius: 8 }}>
            <FileText size={20} />
          </div>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800 }}>{sops.length}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Total Procedures</div>
          </div>
        </div>
        <div className="search-bar" style={{ alignSelf: 'center' }}>
          <Search size={16} color="var(--text-muted)" />
          <input 
            placeholder="Search procedure titles..." 
            value={search} 
            onChange={e => setSearch(e.target.value)} 
          />
        </div>
      </div>

      {/* Content */}
      <div className="card">
        {loading ? (
          <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}><div className="spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <BookOpen size={40} className="empty-icon" />
            <div className="empty-title">No procedures found</div>
            <p>Develop your first SOP to improve field consistency.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Procedure Name</th>
                  <th>Last Updated</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(sop => (
                  <tr key={sop.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ color: 'var(--accent)' }}><FileText size={18} /></div>
                        <span style={{ fontWeight: 600 }}>{sop.title}</span>
                      </div>
                    </td>
                    <td>
                      <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                        {new Date(sop.updated_at).toLocaleDateString()}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                        <button 
                          className="btn-icon" 
                          onClick={() => { setEditingSop(sop); setShowModal(true); }}
                          title="Edit Procedure"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button 
                          className="btn-icon" 
                          style={{ color: 'var(--danger)' }} 
                          onClick={() => handleDelete(sop.id)}
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <SOPModal 
          sop={editingSop} 
          onClose={() => setShowModal(false)} 
          onSave={() => { setShowModal(false); load(); }} 
        />
      )}
    </div>
  );
}
